// services/WebSocketService.ts
import { EventEmitter } from 'events';

// Соответствует C# enum
export enum MessageType {
    // === СИСТЕМНЫЕ СООБЩЕНИЯ (0x0001 - 0x00FF) ===
    Ping = 0x0001,              // Ping-запрос (клиент → сервер)
    Pong = 0x0002,              // Ping-ответ (сервер → клиент)
    Auth = 0x0003,              // Аутентификация
    Error = 0x0004,             // Сообщение об ошибке

    // === ДАННЫЕ УСТРОЙСТВ (0x0101 - 0x01FF) ===
    ScaleData = 0x0101,         // Данные с весов
    ScaleConfig = 0x0102,       // Конфигурация весов
    ScaleStatus = 0x0103,       // Статус весов

    // === КОМАНДЫ УПРАВЛЕНИЯ (0x0201 - 0x02FF) ===
    Command = 0x0201,           // Команда устройству
    Subscribe = 0x0202,         // Подписка на данные
    Unsubscribe = 0x0203,       // Отписка от данных

    // === ПЕРЕДАЧА ФАЙЛОВ (0x0301 - 0x03FF) ===
    FileStart = 0x0301,         // Начало передачи файла
    FileChunk = 0x0302,         // Чанк файла
    FileEnd = 0x0303,           // Конец передачи файла
}

export interface WebSocketMessage {
    messageType: MessageType;
    protocolVersion: number;
    payloadSize: number;
    payload: Uint8Array;
}

class WebSocketService extends EventEmitter {
    private static instance: WebSocketService;
    private socket: WebSocket | null = null;
    private pendingRequests: Map<number, { resolve: Function; reject: Function }> = new Map();
    private requestId = 0;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectInterval = 3000;
    private pingInterval: NodeJS.Timeout | null = null;
    // private currentUrl: string = 'wss://localhost:7292/ws';
    private currentUrl: string = 'wss://beehoney.by/ws';


    private constructor() {
        super();
    }

    static getInstance(): WebSocketService {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService();
        }
        return WebSocketService.instance;
    }

    async connect(url?: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.socket?.readyState === WebSocket.OPEN) {
                resolve();
                return;
            }

            this.socket = new WebSocket(this.currentUrl);
            this.socket.binaryType = 'arraybuffer';

            this.socket.onopen = () => {
                console.log('WebSocket connected');
                this.reconnectAttempts = 0;

                // Запускаем ping
                this.startPing();

                // Аутентификация при подключении
                const token = localStorage.getItem('token');
                if (token) {
                    this.authenticate(token);
                }

                this.emit('connected');
                resolve();
            };

            this.socket.onmessage = (event) => {
                if (event.data instanceof ArrayBuffer) {
                    this.handleBinaryMessage(new Uint8Array(event.data));
                } else {
                    console.error('Unsupported message type:', typeof event.data);
                }
            };

            this.socket.onerror = (error) => {
                console.error('WebSocket error:', error);
                this.emit('error', error);
                reject(error);
            };

            this.socket.onclose = (event) => {
                console.log('WebSocket disconnected:', event.code, event.reason);
                this.stopPing();
                this.emit('disconnected');
                this.handleReconnection();
            };
        });
    }

    private handleReconnection(): void {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = this.calculateReconnectDelay();

            console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);
            this.emit('reconnecting', {
                attempt: this.reconnectAttempts,
                maxAttempts: this.maxReconnectAttempts,
                delay
            });

            setTimeout(() => {

                this.connect(this.currentUrl).catch((error) => {
                    console.error('Reconnection failed:', error);
                    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                        this.emit('reconnection_failed');
                    }
                });

            }, delay);
        } else {
            console.error('Max reconnection attempts reached');
            this.emit('reconnection_failed');
        }
    }

    private calculateReconnectDelay(): number {
        // Exponential backoff with jitter
        const baseDelay = this.reconnectInterval;
        const maxDelay = 30000; // 30 seconds
        const exponentialDelay = baseDelay * Math.pow(1.5, this.reconnectAttempts - 1);
        const jitter = Math.random() * 1000; // Add random jitter
        return Math.min(exponentialDelay + jitter, maxDelay);
    }

    private startPing(): void {
        this.pingInterval = setInterval(() => {
            if (this.isConnected()) {
                this.sendMessage(MessageType.Ping, 1, new Uint8Array());
            }
        }, 30000); // Ping каждые 30 секунд
    }

    private stopPing(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    private async authenticate(token: string): Promise<void> {
        try {
            const payload = this.stringToBytes(token);
            await this.sendRequest(MessageType.Auth, payload);
            console.log('Authentication successful');
        } catch (error) {
            console.error('Authentication failed:', error);
            this.emit('auth_failed');
        }
    }

    private handleBinaryMessage(data: Uint8Array): void {
        try {
            const message = this.parseMessage(data);
            this.handleParsedMessage(message);
        } catch (error) {
            console.error('Error parsing binary message:', error);
        }
    }

    private parseMessage(data: Uint8Array): WebSocketMessage {
        const dataView = new DataView(data.buffer);

        // Чтение заголовка (8 байт)
        const messageType = dataView.getUint16(0, true) as MessageType;
        const protocolVersion = dataView.getUint16(2, true);
        const payloadSize = dataView.getUint32(4, true);

        // Чтение payload
        const payload = data.slice(8, 8 + payloadSize);

        return {
            messageType,
            protocolVersion,
            payloadSize,
            payload
        };
    }

    private serializeMessage(messageType: MessageType, protocolVersion: number, payload: Uint8Array): Uint8Array {
        const headerSize = 8;
        const fullMessage = new Uint8Array(headerSize + payload.length);
        const dataView = new DataView(fullMessage.buffer);

        // Запись заголовка
        dataView.setUint16(0, messageType, true);
        dataView.setUint16(2, protocolVersion, true);
        dataView.setUint32(4, payload.length, true);

        // Запись payload
        fullMessage.set(payload, headerSize);

        return fullMessage;
    }

    private handleParsedMessage(message: WebSocketMessage): void {
        const { messageType, payload } = message;

        // Обработка системных сообщений
        switch (messageType) {
            case MessageType.Pong:
                this.emit('pong');
                return;

            case MessageType.Error:
                const errorMessage = this.bytesToString(payload);
                this.emit('error_message', errorMessage);
                return;

            case MessageType.Ping:
                // Ответ на ping от сервера
                this.sendMessage(MessageType.Pong, 1, new Uint8Array());
                return;
        }

        // Обработка ответов на запросы
        if (this.pendingRequests.has(messageType)) {
            const { resolve, reject } = this.pendingRequests.get(messageType)!;
            this.pendingRequests.delete(messageType);


            resolve(payload);

            return;
        }

        // Обработка push-уведомлений
        this.emit(`message_${messageType}`, payload);
        this.emit('message', message);
    }

    // Public API
    async sendRequest(messageType: MessageType, payload: Uint8Array = new Uint8Array(), protocolVersion: number = 1): Promise<Uint8Array> {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket is not connected');
        }

        return new Promise((resolve, reject) => {
            this.pendingRequests.set(messageType, { resolve, reject });

            const message = this.serializeMessage(messageType, protocolVersion, payload);
            this.socket!.send(message);

            // Таймаут запроса
            setTimeout(() => {
                if (this.pendingRequests.has(messageType)) {
                    this.pendingRequests.delete(messageType);
                    reject(new Error('Request timeout'));
                }
            }, 30000);
        });
    }

    // Отправка сообщения без ожидания ответа
    sendMessage(messageType: MessageType, protocolVersion: number = 1, payload: Uint8Array = new Uint8Array()): void {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket is not connected');
        }

        const message = this.serializeMessage(messageType, protocolVersion, payload);
        this.socket.send(message);
    }

    // Подписка на данные устройств
    subscribe(deviceId: string): void {
        const payload = this.stringToBytes(deviceId);
        this.sendMessage(MessageType.Subscribe, 1, payload);
    }

    unsubscribe(deviceId: string): void {
        const payload = this.stringToBytes(deviceId);
        this.sendMessage(MessageType.Unsubscribe, 1, payload);
    }

    // Отправка команды устройству
    sendCommand(deviceId: string, command: any): void {
        const commandData = this.objectToBytes({ deviceId, command });
        this.sendMessage(MessageType.Command, 1, commandData);
    }

    // Утилиты для работы с бинарными данными
    private stringToBytes(str: string): Uint8Array {
        return new TextEncoder().encode(str);
    }

    private bytesToString(bytes: Uint8Array): string {
        return new TextDecoder().decode(bytes);
    }

    private objectToBytes(obj: any): Uint8Array {
        return this.stringToBytes(JSON.stringify(obj));
    }

    private bytesToObject<T>(bytes: Uint8Array): T {
        return JSON.parse(this.bytesToString(bytes));
    }

    isConnected(): boolean {
        return this.socket?.readyState === WebSocket.OPEN;
    }

    disconnect(): void {
        this.stopPing();
        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }
        this.pendingRequests.clear();
    }

    onMessage(messageType: MessageType, callback: (data: Uint8Array) => void): void {
        this.on(`message_${messageType}`, callback);
    }

    offMessage(messageType: MessageType, callback: (data: Uint8Array) => void): void {
        this.off(`message_${messageType}`, callback);
    }

    onAnyMessage(callback: (message: WebSocketMessage) => void): void {
        this.on('message', callback);
    }
}

export const wsService = WebSocketService.getInstance();
export default wsService;